import React, { useState, useRef, useEffect } from 'react';
import {
  Box,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Typography,
  Paper,
  Chip,
  CircularProgress,
  Tooltip,
  Avatar,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Alert
} from '@mui/material';
import {
  SmartToy,
  Send,
  Close,
  Chat,
  Help,
  Lightbulb,
  QuestionAnswer,
  Psychology,
  AutoAwesome
} from '@mui/icons-material';
import { generateContent } from '../../services/AIService';

/**
 * Section AI Assistant Component
 * 
 * A floating AI assistant that can be integrated into each section corner
 * to help users interact with generated content and ask questions.
 * The assistant has context of all sections and uses Gemini for intelligent responses.
 */

const SectionAIAssistant = ({
  sectionTitle,
  sectionContent,
  allSections = [],
  onClose = () => {},
  open = false,
  position = 'bottom-right' // 'top-left', 'top-right', 'bottom-left', 'bottom-right'
}) => {
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isTyping, setIsTyping] = useState(false);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  // Initialize with welcome message
  useEffect(() => {
    if (open && messages.length === 0) {
      setMessages([{
        id: 1,
        type: 'assistant',
        content: `Hello! I'm your AI assistant for ${sectionTitle}. I can help you understand, improve, or ask questions about this section's content. I also have context of all other sections in your protocol. How can I assist you today?`,
        timestamp: new Date()
      }]);
    }
  }, [open, sectionTitle, messages.length]);

  // Auto-scroll to bottom when new messages arrive
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Focus input when dialog opens
  useEffect(() => {
    if (open && inputRef.current) {
      setTimeout(() => inputRef.current.focus(), 100);
    }
  }, [open]);

  const handleSendMessage = async () => {
    if (!inputMessage.trim() || isLoading) return;

    const userMessage = {
      id: Date.now(),
      type: 'user',
      content: inputMessage.trim(),
      timestamp: new Date()
    };

    setMessages(prev => [...prev, userMessage]);
    setInputMessage('');
    setIsLoading(true);
    setIsTyping(true);

    try {
      // Create context for the AI assistant
      const context = createAssistantContext(sectionTitle, sectionContent, allSections);
      
      // Create a comprehensive prompt for the AI assistant
      const assistantPrompt = `You are an expert AI assistant specialized in clinical trial protocol development. You are helping a user with ${sectionTitle} and have access to the full protocol context.

# YOUR ROLE
- Provide expert guidance on clinical trial protocol sections
- Help users understand complex protocol content
- Suggest improvements and best practices
- Answer questions about regulatory requirements
- Assist with content structure and formatting
- Provide context from other sections when relevant

# CURRENT SECTION CONTEXT
Section: ${sectionTitle}
Content: ${sectionContent}

# FULL PROTOCOL CONTEXT
${context}

# USER QUESTION
${inputMessage.trim()}

# INSTRUCTIONS
- Provide helpful, accurate, and professional responses
- Be CONCISE and DIRECT - answer the question without unnecessary details
- For simple questions (like "which drug"), give a one-line answer
- Only provide detailed explanations when specifically requested
- Reference specific content only when relevant to the question
- Suggest improvements if appropriate
- Use clinical trial terminology appropriately
- If the question is about another section, provide context from that section
- Always maintain a helpful and professional tone

# RESPONSE FORMAT
Provide a clear, structured response that directly addresses the user's question. If suggesting improvements, be specific about what could be enhanced.

# IMPORTANT: RESPONSE FORMATTING
- Use plain text only (NO HTML tags)
- Use **bold** for emphasis (not <strong> tags)
- Use *italics* for emphasis (not <em> tags)
- Use bullet points with • for lists
- Use numbered lists with 1. 2. 3. for ordered lists
- Use > for blockquotes
- Keep responses conversational and professional

# RESPONSE LENGTH GUIDELINES
- Simple questions (what drug, what phase, etc.): 1-2 sentences maximum
- Complex questions (explain methodology, etc.): Provide detailed but concise answers
- Only include quotes or section references when specifically relevant
- Avoid unnecessary repetition or over-explanation`;

      const response = await generateContent(
        assistantPrompt,
        context,
        '',
        'assistant',
        `${sectionTitle} AI Assistant`
      );

      const assistantMessage = {
        id: Date.now() + 1,
        type: 'assistant',
        content: response,
        timestamp: new Date()
      };

      setMessages(prev => [...prev, assistantMessage]);

    } catch (error) {
      console.error('Error getting AI response:', error);
      const errorMessage = {
        id: Date.now() + 1,
        type: 'assistant',
        content: 'I apologize, but I encountered an error while processing your request. Please try again or rephrase your question.',
        timestamp: new Date(),
        isError: true
      };
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setIsLoading(false);
      setIsTyping(false);
    }
  };

  const createAssistantContext = (currentSectionTitle, currentSectionContent, allSections) => {
    let context = `# PROTOCOL SECTIONS OVERVIEW\n\n`;
    
    allSections.forEach((section, index) => {
      const isCurrentSection = section.title === currentSectionTitle;
      context += `## ${section.title}${isCurrentSection ? ' (CURRENT SECTION)' : ''}\n`;
      if (section.output && section.output.trim()) {
        // Truncate content if too long
        const content = section.output.length > 1000 
          ? section.output.substring(0, 1000) + '...' 
          : section.output;
        context += `${content}\n\n`;
      } else {
        context += `[Content not yet generated]\n\n`;
      }
    });

    return context;
  };

  const formatAIResponse = (content) => {
    if (!content) return '';
    
    // Remove HTML tags and clean up formatting
    let formatted = content
      .replace(/<p>/g, '')
      .replace(/<\/p>/g, '\n\n')
      .replace(/<strong>/g, '**')
      .replace(/<\/strong>/g, '**')
      .replace(/<em>/g, '*')
      .replace(/<\/em>/g, '*')
      .replace(/<blockquote>/g, '\n> ')
      .replace(/<\/blockquote>/g, '\n')
      .replace(/<ul>/g, '\n')
      .replace(/<li>/g, '• ')
      .replace(/<\/li>/g, '\n')
      .replace(/<\/ul>/g, '\n')
      .replace(/<ol>/g, '\n')
      .replace(/<li>/g, '1. ')
      .replace(/<\/li>/g, '\n')
      .replace(/<\/ol>/g, '\n')
      .replace(/<br\s*\/?>/g, '\n')
      .replace(/<[^>]*>/g, '') // Remove any remaining HTML tags
      .replace(/\n{3,}/g, '\n\n') // Clean up multiple newlines
      .trim();
    
    return formatted;
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const getPositionStyles = () => {
    switch (position) {
      case 'top-left':
        return { top: 16, left: 16 };
      case 'top-right':
        return { top: 16, right: 16 };
      case 'bottom-left':
        return { bottom: 16, left: 16 };
      case 'bottom-right':
      default:
        return { bottom: 16, right: 16 };
    }
  };

  const suggestedQuestions = [
    "What are the key points in this section?",
    "How can I improve this content?",
    "What regulatory requirements apply?",
    "How does this relate to other sections?",
    "What's missing from this section?",
    "Can you explain this in simpler terms?"
  ];

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          position: 'fixed',
          ...getPositionStyles(),
          width: '500px',
          height: '600px',
          maxHeight: '80vh',
          m: 0,
          borderRadius: 2,
          boxShadow: '0 8px 32px rgba(0,0,0,0.12)'
        }
      }}
    >
      <DialogTitle sx={{ 
        display: 'flex', 
        alignItems: 'center', 
        gap: 1, 
        pb: 1,
        borderBottom: '1px solid #e0e0e0'
      }}>
        <Avatar sx={{ bgcolor: 'primary.main', width: 32, height: 32 }}>
          <SmartToy />
        </Avatar>
        <Box sx={{ flex: 1 }}>
          <Typography variant="h6" component="div">
            AI Assistant
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {sectionTitle}
          </Typography>
        </Box>
        <IconButton onClick={onClose} size="small">
          <Close />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: 0, display: 'flex', flexDirection: 'column' }}>
        {/* Messages Area */}
        <Box sx={{ 
          flex: 1, 
          overflow: 'auto', 
          p: 2, 
          minHeight: '400px',
          maxHeight: '400px'
        }}>
          {messages.map((message) => (
            <Box
              key={message.id}
              sx={{
                display: 'flex',
                justifyContent: message.type === 'user' ? 'flex-end' : 'flex-start',
                mb: 2
              }}
            >
              <Paper
                sx={{
                  p: 2,
                  maxWidth: '80%',
                  bgcolor: message.type === 'user' 
                    ? 'primary.main' 
                    : message.isError 
                      ? 'error.light' 
                      : 'grey.100',
                  color: message.type === 'user' ? 'white' : 'text.primary',
                  borderRadius: 2,
                  ...(message.type === 'user' && {
                    borderTopRightRadius: 4
                  }),
                  ...(message.type === 'assistant' && {
                    borderTopLeftRadius: 4
                  })
                }}
              >
                <Typography 
                  variant="body2" 
                  sx={{ whiteSpace: 'pre-wrap' }}
                >
                  {formatAIResponse(message.content)}
                </Typography>
                <Typography 
                  variant="caption" 
                  sx={{ 
                    display: 'block', 
                    mt: 1, 
                    opacity: 0.7,
                    fontSize: '0.75rem'
                  }}
                >
                  {message.timestamp.toLocaleTimeString()}
                </Typography>
              </Paper>
            </Box>
          ))}
          
          {isTyping && (
            <Box sx={{ display: 'flex', justifyContent: 'flex-start', mb: 2 }}>
              <Paper sx={{ p: 2, bgcolor: 'grey.100', borderRadius: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CircularProgress size={16} />
                  <Typography variant="body2" color="text.secondary">
                    AI is thinking...
                  </Typography>
                </Box>
              </Paper>
            </Box>
          )}
          
          <div ref={messagesEndRef} />
        </Box>

        {/* Suggested Questions */}
        {messages.length === 1 && (
          <Box sx={{ p: 2, borderTop: '1px solid #e0e0e0' }}>
            <Typography variant="subtitle2" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Lightbulb fontSize="small" />
              Suggested Questions
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {suggestedQuestions.map((question, index) => (
                <Chip
                  key={index}
                  label={question}
                  size="small"
                  onClick={() => setInputMessage(question)}
                  sx={{ cursor: 'pointer', fontSize: '0.75rem' }}
                />
              ))}
            </Box>
          </Box>
        )}

        {/* Input Area */}
        <Box sx={{ p: 2, borderTop: '1px solid #e0e0e0' }}>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <TextField
              ref={inputRef}
              fullWidth
              multiline
              maxRows={3}
              placeholder="Ask me anything about this section..."
              value={inputMessage}
              onChange={(e) => setInputMessage(e.target.value)}
              onKeyPress={handleKeyPress}
              disabled={isLoading}
              size="small"
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
            />
            <Button
              variant="contained"
              onClick={handleSendMessage}
              disabled={!inputMessage.trim() || isLoading}
              sx={{ 
                minWidth: 'auto', 
                px: 2,
                borderRadius: 2
              }}
            >
              {isLoading ? <CircularProgress size={20} /> : <Send />}
            </Button>
          </Box>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default SectionAIAssistant;
